import { useEffect, useMemo, useState } from 'react';
import api from '../lib/api';
import { FileSignature, Plus, Printer, Trash2, Loader } from 'lucide-react';

interface Avalista {
  name?: string;
  type?: string;
  document?: string;
  rg?: string;
  address?: string;
}

interface Note {
  id: string;
  number: number;
  status?: string;
  debtorName?: string;
  debtorDocument?: string;
  creditorType?: string;
  creditorName?: string;
  principal: number | string;
  installments: number;
  interestType: string;
  interestRate: number | string;
  totalAmount: number | string;
  installmentAmount: number | string;
  emissionDate: string;
  firstDueDate: string;
  cityUf?: string;
}

const money = (v: any) => 'R$ ' + (Number(v) || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 });
const round2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;
const esc = (s: any) => String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

// Valor por extenso (reais e centavos) em português.
const _U = ['', 'um', 'dois', 'três', 'quatro', 'cinco', 'seis', 'sete', 'oito', 'nove', 'dez', 'onze', 'doze', 'treze', 'quatorze', 'quinze', 'dezesseis', 'dezessete', 'dezoito', 'dezenove'];
const _D = ['', '', 'vinte', 'trinta', 'quarenta', 'cinquenta', 'sessenta', 'setenta', 'oitenta', 'noventa'];
const _C = ['', 'cento', 'duzentos', 'trezentos', 'quatrocentos', 'quinhentos', 'seiscentos', 'setecentos', 'oitocentos', 'novecentos'];
function _trio(n: number): string {
  if (n === 0) return '';
  if (n === 100) return 'cem';
  let s = '';
  const c = Math.floor(n / 100);
  const r = n % 100;
  if (c) s += _C[c];
  if (r) {
    if (s) s += ' e ';
    if (r < 20) s += _U[r];
    else {
      s += _D[Math.floor(r / 10)];
      if (r % 10) s += ' e ' + _U[r % 10];
    }
  }
  return s;
}
function _intExtenso(n: number): string {
  if (n === 0) return 'zero';
  const mi = Math.floor(n / 1000000);
  const mil = Math.floor((n % 1000000) / 1000);
  const resto = n % 1000;
  const parts: string[] = [];
  if (mi) parts.push(_trio(mi) + (mi === 1 ? ' milhão' : ' milhões'));
  if (mil) parts.push(mil === 1 ? 'mil' : _trio(mil) + ' mil');
  if (resto) parts.push(_trio(resto));
  return parts.join(' e ');
}
function extenso(v: number): string {
  const inteiro = Math.floor(v);
  const cent = Math.round((v - inteiro) * 100);
  let s = `${_intExtenso(inteiro)} ${inteiro === 1 ? 'real' : 'reais'}`;
  if (cent > 0) s += ` e ${_intExtenso(cent)} ${cent === 1 ? 'centavo' : 'centavos'}`;
  return s;
}

function computeTotal(principal: number, n: number, type: string, ratePct: number) {
  const i = (ratePct || 0) / 100;
  if (type === 'SIMPLE' && i > 0) return round2(principal * (1 + i * n));
  if (type === 'COMPOUND' && i > 0) {
    const pmt = (principal * i) / (1 - Math.pow(1 + i, -n));
    return round2(round2(pmt) * n);
  }
  return round2(principal);
}

const EMPTY_AVALISTA: any = { type: 'FISICA', addr: {} };

// Endereço estruturado da pessoa (1º/primário) -> objeto do formulário.
function addrFromPerson(p: any) {
  const a = (p?.addresses || []).find((x: any) => x.isPrimary) || (p?.addresses || [])[0];
  if (!a) return {};
  return { cep: a.zipCode || '', street: a.street || '', number: a.number || '', neighborhood: a.neighborhood || '', city: a.city || '', state: a.state || '' };
}

function composeAddr(a: any): string {
  if (!a) return '';
  const ruaNum = [a.street, a.number].filter(Boolean).join(', ');
  return [ruaNum, a.neighborhood, [a.city, a.state].filter(Boolean).join('/'), a.cep ? `CEP: ${a.cep}` : '']
    .filter(Boolean)
    .join(', ');
}

const onlyDigits = (s: any) => String(s ?? '').replace(/\D/g, '');
const formatBRL = (n: number) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number.isFinite(Number(n)) ? Number(n) : 0);
const parseBRL = (s: string) => {
  const c = String(s || '').replace(/\s/g, '').replace(/[R$]/g, '').replace(/\./g, '').replace(/,/g, '.').replace(/[^0-9.-]/g, '');
  const n = Number(c);
  return Number.isFinite(n) ? n : 0;
};

function maskCPF(d: string) {
  d = d.slice(0, 11);
  if (d.length > 9) return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
  if (d.length > 6) return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6)}`;
  if (d.length > 3) return `${d.slice(0, 3)}.${d.slice(3)}`;
  return d;
}
function maskCNPJ(d: string) {
  d = d.slice(0, 14);
  if (d.length > 12) return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}`;
  if (d.length > 8) return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8)}`;
  if (d.length > 5) return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5)}`;
  if (d.length > 2) return `${d.slice(0, 2)}.${d.slice(2)}`;
  return d;
}
const maskDoc = (value: string, type?: string) => (type === 'JURIDICA' ? maskCNPJ(onlyDigits(value)) : maskCPF(onlyDigits(value)));

function isValidCPF(value: string) {
  const c = onlyDigits(value);
  if (c.length !== 11 || /^(\d)\1{10}$/.test(c)) return false;
  let s = 0;
  for (let i = 0; i < 9; i++) s += +c[i] * (10 - i);
  let r = (s * 10) % 11 % 10;
  if (r !== +c[9]) return false;
  s = 0;
  for (let i = 0; i < 10; i++) s += +c[i] * (11 - i);
  r = (s * 10) % 11 % 10;
  return r === +c[10];
}

function AddressBlock({ value, onChange }: { value: any; onChange: (v: any) => void }) {
  const v = value || {};
  const [busy, setBusy] = useState(false);
  const f = (k: string) => (e: any) => onChange({ ...v, [k]: e.target.value });
  // Busca o endereço no ViaCEP a partir do valor do campo (não do estado, p/ evitar defasagem).
  async function lookupCep(rawCep: string) {
    const cep = onlyDigits(rawCep);
    if (cep.length !== 8) return;
    setBusy(true);
    try {
      const r = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
      const d = await r.json();
      if (!d.erro) {
        onChange({
          ...v,
          cep: rawCep,
          street: d.logradouro || v.street || '',
          neighborhood: d.bairro || v.neighborhood || '',
          city: d.localidade || v.city || '',
          state: d.uf || v.state || '',
        });
      }
    } catch {
      /* ignore */
    } finally {
      setBusy(false);
    }
  }
  function onCepChange(e: any) {
    const val = e.target.value;
    onChange({ ...v, cep: val });
    if (onlyDigits(val).length === 8) lookupCep(val);
  }
  return (
    <div className="grid md:grid-cols-6 gap-3">
      <div className="md:col-span-1">
        <input
          value={v.cep || ''}
          onChange={onCepChange}
          onBlur={(e) => lookupCep(e.target.value)}
          placeholder="CEP"
          className="input"
        />
        {busy && <span className="text-[10px] text-gray-400">buscando…</span>}
      </div>
      <input value={v.street || ''} onChange={f('street')} placeholder="Logradouro" className="input md:col-span-4" />
      <input value={v.number || ''} onChange={f('number')} placeholder="Nº" className="input md:col-span-1" />
      <input value={v.neighborhood || ''} onChange={f('neighborhood')} placeholder="Bairro" className="input md:col-span-2" />
      <input value={v.city || ''} onChange={f('city')} placeholder="Cidade" className="input md:col-span-3" />
      <input value={v.state || ''} onChange={f('state')} maxLength={2} placeholder="UF" className="input md:col-span-1" />
    </div>
  );
}

export default function PromissoryPage() {
  const [notes, setNotes] = useState<Note[]>([]);
  const [people, setPeople] = useState<any[]>([]);
  const [leases, setLeases] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [show, setShow] = useState(false);
  const [principalText, setPrincipalText] = useState('');
  const [delTarget, setDelTarget] = useState<Note | null>(null);
  const [delForm, setDelForm] = useState({ reason: '', pin: '' });
  const [cancelTarget, setCancelTarget] = useState<Note | null>(null);
  const [cancelForm, setCancelForm] = useState({ reason: '', method: 'PIX', amount: '', discount: '', pin: '' });
  const [busyAction, setBusyAction] = useState(false);

  const today = new Date().toISOString().slice(0, 10);
  const nextMonth = new Date(Date.now() + 30 * 864e5).toISOString().slice(0, 10);

  const [form, setForm] = useState<any>({
    principal: '',
    installments: 1,
    interestType: 'NONE',
    interestRate: 0,
    emissionDate: today,
    firstDueDate: nextMonth,
    cityUf: '',
    observations: '',
    contractNumber: '',
    leaseId: '',
    creditorType: 'IMOBILIARIA',
    creditorName: '',
    creditorDocument: '',
    creditorAddr: {},
    debtorName: '',
    debtorType: 'FISICA',
    debtorDocument: '',
    debtorRg: '',
    debtorAddr: {},
    avalistas: [{ ...EMPTY_AVALISTA }],
  });

  function set(k: string, v: any) {
    setForm((f: any) => ({ ...f, [k]: v }));
  }
  function setAval(idx: number, k: string, v: any) {
    setForm((f: any) => {
      const arr = [...(f.avalistas || [])];
      arr[idx] = { ...arr[idx], [k]: v };
      return { ...f, avalistas: arr };
    });
  }

  const preview = useMemo(() => {
    const p = Number(form.principal) || 0;
    const n = Math.max(1, parseInt(form.installments, 10) || 1);
    const total = computeTotal(p, n, form.interestType, Number(form.interestRate) || 0);
    return { total, parcela: round2(total / n), n };
  }, [form.principal, form.installments, form.interestType, form.interestRate]);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get('/promissory');
      setNotes(data || []);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    load();
    api
      .get('/people', { params: { limit: 1000 } })
      .then(({ data }) => setPeople(data?.data || []))
      .catch(() => setPeople([]));
    api
      .get('/leases', { params: { limit: 1000 } })
      .then(({ data }) => setLeases(data?.data || []))
      .catch(() => setLeases([]));
  }, []);

  // Preenche o emitente a partir de uma pessoa do cadastro.
  function pickDebtor(personId: string) {
    const p = people.find((x) => x.id === personId);
    if (!p) {
      setForm((f: any) => ({ ...f, personId: '' }));
      return;
    }
    const type = p.type === 'COMPANY' ? 'JURIDICA' : 'FISICA';
    setForm((f: any) => ({
      ...f,
      personId: p.id,
      debtorName: p.name || '',
      debtorType: type,
      debtorDocument: maskDoc(p.document || '', type),
      debtorAddr: addrFromPerson(p),
    }));
  }

  // Preenche o credor (terceiro) a partir de uma pessoa do cadastro.
  function pickCreditor(personId: string) {
    const p = people.find((x) => x.id === personId);
    if (!p) return;
    const type = p.type === 'COMPANY' ? 'JURIDICA' : 'FISICA';
    setForm((f: any) => ({
      ...f,
      creditorName: p.name || '',
      creditorDocument: maskDoc(p.document || '', type),
      creditorAddr: addrFromPerson(p),
    }));
  }

  // Preenche um avalista a partir de uma pessoa do cadastro.
  function pickAval(idx: number, personId: string) {
    const p = people.find((x) => x.id === personId);
    if (!p) return;
    const type = p.type === 'COMPANY' ? 'JURIDICA' : 'FISICA';
    setForm((f: any) => {
      const arr = [...(f.avalistas || [])];
      arr[idx] = {
        ...arr[idx],
        name: p.name || '',
        type,
        document: maskDoc(p.document || '', type),
        addr: addrFromPerson(p),
      };
      return { ...f, avalistas: arr };
    });
  }

  function pickLease(leaseId: string) {
    const l = leases.find((x) => x.id === leaseId);
    setForm((f: any) => ({ ...f, leaseId: l ? l.id : '', contractNumber: l ? l.code || f.contractNumber : f.contractNumber }));
  }

  async function create() {
    if (!Number(form.principal) || Number(form.principal) <= 0) {
      alert('Informe o valor.');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        principal: Number(form.principal),
        installments: Number(form.installments),
        interestRate: Number(form.interestRate),
        debtorAddress: composeAddr(form.debtorAddr),
        creditorAddress: composeAddr(form.creditorAddr),
        avalistas: (form.avalistas || []).map((a: any) => ({
          name: a.name,
          type: a.type,
          document: a.document,
          rg: a.rg,
          address: composeAddr(a.addr),
        })),
      };
      await api.post('/promissory', payload);
      setShow(false);
      setPrincipalText('');
      setForm((f: any) => ({
        ...f,
        principal: '',
        observations: '',
        contractNumber: '',
        leaseId: '',
        debtorName: '',
        debtorDocument: '',
        debtorRg: '',
        debtorAddr: {},
        personId: '',
        avalistas: [{ ...EMPTY_AVALISTA }],
      }));
      await load();
      alert(form.creditorType === 'TERCEIRO' ? 'Nota promissória criada (só impressão).' : 'Parcelamento criado. As parcelas entraram em Contas a Receber.');
    } catch (e: any) {
      alert(e?.response?.data?.message || 'Falha ao criar.');
    } finally {
      setSaving(false);
    }
  }

  async function confirmDelete() {
    if (!delTarget) return;
    if (!delForm.reason.trim()) return alert('Informe a justificativa.');
    if (!delForm.pin) return alert('Informe o PIN financeiro.');
    setBusyAction(true);
    try {
      await api.delete(`/promissory/${delTarget.id}`, { data: { reason: delForm.reason, pin: delForm.pin } });
      setDelTarget(null);
      setDelForm({ reason: '', pin: '' });
      await load();
      alert('Nota apagada e parcelas desfeitas.');
    } catch (e: any) {
      alert(e?.response?.data?.message || 'Falha ao apagar.');
    } finally {
      setBusyAction(false);
    }
  }

  async function confirmCancel() {
    if (!cancelTarget) return;
    if (!cancelForm.reason.trim()) return alert('Informe a justificativa da quitação.');
    if (!cancelForm.pin) return alert('Informe o PIN financeiro.');
    setBusyAction(true);
    try {
      await api.post(`/promissory/${cancelTarget.id}/cancel`, {
        reason: cancelForm.reason,
        method: cancelForm.method,
        amount: cancelForm.amount ? parseBRL(cancelForm.amount) : undefined,
        discount: cancelForm.discount ? parseBRL(cancelForm.discount) : undefined,
        pin: cancelForm.pin,
      });
      setCancelTarget(null);
      setCancelForm({ reason: '', method: 'PIX', amount: '', discount: '', pin: '' });
      await load();
      alert('Nota quitada. Recebimento lançado no financeiro.');
    } catch (e: any) {
      alert(e?.response?.data?.message || 'Falha ao quitar.');
    } finally {
      setBusyAction(false);
    }
  }

  async function print(id: string) {
    const { data } = await api.get(`/promissory/${id}`);
    const list = data.installmentsList || [];
    const win = window.open('', '_blank', 'width=820,height=900');
    if (!win) {
      alert('Permita pop-ups para imprimir.');
      return;
    }
    const aval = Array.isArray(data.avalistas) ? data.avalistas : [];
    const emissao = new Date(data.emissionDate).toLocaleDateString('pt-BR');
    const docLabel = data.debtorType === 'JURIDICA' ? 'CNPJ' : 'CPF';
    const credor = `${esc(data.creditorName || '')}${data.creditorDocument ? `, inscrito(a) no CPF/CNPJ sob nº ${esc(data.creditorDocument)}` : ''}`;

    const notesHtml = list
      .map((inst: any, idx: number) => {
        const venc = new Date(inst.dueDate).toLocaleDateString('pt-BR');
        const valor = Number(inst.amount) || 0;
        return `
        <div class="np">
          <div class="np-top">
            <div class="np-id">NOTA PROMISSÓRIA<br/><span>Nº ${idx + 1} / ${data.installments}</span></div>
            <table class="np-box">
              <tr><td class="l">Vencimento</td><td class="v">${venc}</td></tr>
              <tr><td class="l">Valor</td><td class="v big">${money(valor)}</td></tr>
            </table>
          </div>
          ${data.contractNumber ? `<div class="np-contract">Referente ao Contrato nº ${esc(data.contractNumber)}</div>` : ''}
          <p class="np-body">
            Ao(s) <b>${venc}</b> pagarei(emos) por esta única via de NOTA PROMISSÓRIA a
            <b>${credor || '—'}</b>, ou à sua ordem, a quantia de <b>${money(valor)}</b>
            (<i>${extenso(valor)}</i>) em moeda corrente nacional${data.cityUf ? `, pagável em ${esc(data.cityUf)}` : ''}.
          </p>
          ${data.observations ? `<p class="np-obs">${esc(data.observations)}</p>` : ''}
          <div class="np-place">${esc(data.cityUf || '')}${data.cityUf ? ', ' : ''}${emissao}</div>
          <div class="np-sign">
            <div class="np-sline">
              <div class="line"></div>
              <div class="who"><b>${esc(data.debtorName || 'Emitente')}</b><br/>
                ${docLabel}: ${esc(data.debtorDocument || '—')}${data.debtorRg ? ` · RG: ${esc(data.debtorRg)}` : ''}<br/>
                <span class="addr">${esc(data.debtorAddress || '')}</span></div>
              <div class="cap">Emitente (devedor)</div>
            </div>
          </div>
          ${
            aval.length
              ? `<div class="np-avals">${aval
                  .map(
                    (a: any) => `<div class="np-sline">
                      <div class="line"></div>
                      <div class="who"><b>${esc(a.name || '')}</b><br/>
                        ${a.type === 'JURIDICA' ? 'CNPJ' : 'CPF'}: ${esc(a.document || '—')}${a.rg ? ` · RG: ${esc(a.rg)}` : ''}<br/>
                        <span class="addr">${esc(a.address || '')}</span></div>
                      <div class="cap">Avalista</div>
                    </div>`,
                  )
                  .join('')}</div>`
              : ''
          }
        </div>`;
      })
      .join('');

    win.document.write(`<html><head><title>Notas Promissórias</title><style>
      *{box-sizing:border-box}
      body{font-family:Georgia,'Times New Roman',serif;color:#111;padding:18px;font-size:13px}
      .np{border:2px solid #1f3a5f;border-radius:6px;padding:18px 20px;margin-bottom:18px;page-break-inside:avoid;position:relative}
      .np-top{display:flex;justify-content:space-between;align-items:flex-start;gap:16px;border-bottom:1px solid #1f3a5f;padding-bottom:10px;margin-bottom:12px}
      .np-id{font-weight:bold;letter-spacing:1px;font-size:15px;color:#1f3a5f}
      .np-id span{font-size:12px;letter-spacing:0;color:#444}
      .np-box{border-collapse:collapse}
      .np-box td{border:1px solid #1f3a5f;padding:4px 10px}
      .np-box td.l{background:#eef3f8;font-size:11px;text-transform:uppercase;color:#345}
      .np-box td.v{font-weight:bold}
      .np-box td.big{font-size:16px}
      .np-contract{font-size:11px;color:#555;margin-bottom:4px;font-weight:bold}
      .np-body{line-height:1.8;margin:10px 0;text-align:justify}
      .np-obs{font-size:12px;color:#444;font-style:italic;margin:6px 0}
      .np-place{margin:18px 0 8px;text-align:right}
      .np-sign{margin-top:18px;display:flex;gap:30px;flex-wrap:wrap}
      .np-avals{margin-top:16px;display:flex;gap:30px;flex-wrap:wrap}
      .np-sline{flex:1;min-width:220px;text-align:center}
      .np-sline .line{border-top:1px solid #333;margin-bottom:4px}
      .np-sline .who{font-size:12px;line-height:1.4}
      .np-sline .who .addr{font-size:11px;color:#555}
      .np-sline .cap{font-size:11px;color:#666;text-transform:uppercase;margin-top:4px}
      @media print{ body{padding:0} }
    </style></head><body onload="window.print()">${notesHtml}</body></html>`);
    win.document.close();
    win.focus();
  }

  const TYPE_LABEL: Record<string, string> = { NONE: 'Sem juros', SIMPLE: 'Juros simples', COMPOUND: 'Juros compostos (Price)' };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <FileSignature className="w-6 h-6" /> Notas Promissórias / Parcelamento
          </h1>
          <p className="text-sm text-gray-500">
            Gera as parcelas como cobranças (Contas a Receber), que aparecem no Painel e nos relatórios financeiros.
          </p>
        </div>
        <button onClick={() => setShow(true)} className="btn-primary flex items-center gap-2">
          <Plus className="w-5 h-5" /> Novo parcelamento
        </button>
      </div>

      {show && (
        <div className="card space-y-4">
          <div className="grid md:grid-cols-4 gap-4">
            <label className="block text-sm">Valor total (R$)
              <input
                type="text"
                inputMode="decimal"
                value={principalText}
                onChange={(e) => {
                  setPrincipalText(e.target.value);
                  set('principal', parseBRL(e.target.value));
                }}
                onFocus={(e) => e.currentTarget.select()}
                onBlur={() => {
                  const n = parseBRL(principalText);
                  set('principal', n);
                  setPrincipalText(n ? formatBRL(n) : '');
                }}
                placeholder="R$ 0,00"
                className="input mt-1"
              />
            </label>
            <label className="block text-sm">Parcelas
              <input type="number" min={1} value={form.installments} onChange={(e) => set('installments', e.target.value)} className="input mt-1" />
            </label>
            <label className="block text-sm">Tipo de juros
              <select value={form.interestType} onChange={(e) => set('interestType', e.target.value)} className="input mt-1">
                <option value="NONE">Sem juros</option>
                <option value="SIMPLE">Juros simples</option>
                <option value="COMPOUND">Juros compostos (Price)</option>
              </select>
            </label>
            <label className="block text-sm">Taxa (% ao mês)
              <input type="number" step="0.01" value={form.interestRate} onChange={(e) => set('interestRate', e.target.value)} disabled={form.interestType === 'NONE'} className="input mt-1 disabled:opacity-50" />
            </label>
            <label className="block text-sm">Emissão
              <input type="date" value={form.emissionDate} onChange={(e) => set('emissionDate', e.target.value)} className="input mt-1" />
            </label>
            <label className="block text-sm">1º vencimento
              <input type="date" value={form.firstDueDate} onChange={(e) => set('firstDueDate', e.target.value)} className="input mt-1" />
            </label>
            <label className="block text-sm">Cidade - UF
              <input value={form.cityUf} onChange={(e) => set('cityUf', e.target.value)} placeholder="Curitiba-PR" className="input mt-1" />
            </label>
            <div className="flex items-end">
              <div className="bg-primary-50 text-primary-800 rounded-lg px-3 py-2 text-sm w-full">
                <b>{preview.n}x</b> de <b>{money(preview.parcela)}</b><br />Total: <b>{money(preview.total)}</b>
              </div>
            </div>
          </div>

          <div className="border-t pt-4">
            <p className="font-medium text-sm mb-2">Contrato (opcional)</p>
            <div className="grid md:grid-cols-3 gap-4">
              <label className="block text-sm md:col-span-2">Vincular a um contrato
                <select value={form.leaseId} onChange={(e) => pickLease(e.target.value)} className="input mt-1">
                  <option value="">Nenhum / informar o número ao lado</option>
                  {leases.map((l) => (
                    <option key={l.id} value={l.id}>
                      {l.code}{l.property?.title ? ` — ${l.property.title}` : ''}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block text-sm">Nº do contrato
                <input value={form.contractNumber} onChange={(e) => set('contractNumber', e.target.value)} placeholder="Ex.: CT-2026-001" className="input mt-1" />
              </label>
            </div>
          </div>

          <div className="border-t pt-4">
            <p className="font-medium text-sm mb-2">Credor (quem vai receber)</p>
            <div className="flex flex-wrap gap-2 mb-2">
              <button
                type="button"
                onClick={() => set('creditorType', 'IMOBILIARIA')}
                className={`px-3 py-2 rounded-lg border text-sm ${form.creditorType === 'IMOBILIARIA' ? 'bg-primary-600 text-white border-primary-600' : 'border-gray-300'}`}
              >
                Imobiliária (entra no financeiro)
              </button>
              <button
                type="button"
                onClick={() => set('creditorType', 'TERCEIRO')}
                className={`px-3 py-2 rounded-lg border text-sm ${form.creditorType === 'TERCEIRO' ? 'bg-primary-600 text-white border-primary-600' : 'border-gray-300'}`}
              >
                Terceiro / dono do imóvel (só impressão)
              </button>
            </div>
            {form.creditorType === 'IMOBILIARIA' ? (
              <p className="text-xs text-gray-500">
                O credor é a imobiliária (matriz). As parcelas viram cobranças em Contas a Receber e entram no Painel e relatórios.
              </p>
            ) : (
              <>
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 mb-2">
                  Promissória particular entre o dono do imóvel e o terceiro. <b>Não entra no financeiro</b> — apenas imprime as {preview.n} promissórias.
                </p>
                <select defaultValue="" onChange={(e) => pickCreditor(e.target.value)} className="input mb-2">
                  <option value="">Puxar credor do cadastro de Pessoas...</option>
                  {people.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name} {p.document ? `— ${p.document}` : ''}
                    </option>
                  ))}
                </select>
                <div className="grid md:grid-cols-4 gap-4 mb-3">
                  <input value={form.creditorName} onChange={(e) => set('creditorName', e.target.value)} placeholder="Nome / Razão social do credor" className="input md:col-span-2" />
                  <input value={form.creditorDocument} onChange={(e) => set('creditorDocument', maskDoc(e.target.value, onlyDigits(e.target.value).length > 11 ? 'JURIDICA' : 'FISICA'))} placeholder="CPF/CNPJ" className="input md:col-span-2" />
                </div>
                <AddressBlock value={form.creditorAddr} onChange={(v) => set('creditorAddr', v)} />
              </>
            )}
          </div>

          <div className="border-t pt-4">
            <p className="font-medium text-sm mb-2">Emitente (devedor)</p>
            <div className="mb-3">
              <select
                value={form.personId || ''}
                onChange={(e) => pickDebtor(e.target.value)}
                className="input"
              >
                <option value="">Puxar do cadastro de Pessoas...</option>
                {people.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name} {p.document ? `— ${p.document}` : ''}
                  </option>
                ))}
              </select>
            </div>
            <div className="grid md:grid-cols-4 gap-4 mb-3">
              <input value={form.debtorName} onChange={(e) => set('debtorName', e.target.value)} placeholder="Nome / Razão social" className="input md:col-span-2" />
              <select
                value={form.debtorType}
                onChange={(e) => {
                  const t = e.target.value;
                  setForm((f: any) => ({ ...f, debtorType: t, debtorDocument: maskDoc(f.debtorDocument, t) }));
                }}
                className="input"
              >
                <option value="FISICA">Pessoa Física</option>
                <option value="JURIDICA">Pessoa Jurídica</option>
              </select>
              <div>
                <input
                  value={form.debtorDocument}
                  onChange={(e) => set('debtorDocument', maskDoc(e.target.value, form.debtorType))}
                  placeholder={form.debtorType === 'JURIDICA' ? 'CNPJ' : 'CPF'}
                  className={`input ${form.debtorType === 'FISICA' && form.debtorDocument && !isValidCPF(form.debtorDocument) ? 'border-red-400' : ''}`}
                />
                {form.debtorType === 'FISICA' && form.debtorDocument && !isValidCPF(form.debtorDocument) && (
                  <span className="text-[10px] text-red-500">CPF inválido</span>
                )}
              </div>
              <input value={form.debtorRg} onChange={(e) => set('debtorRg', e.target.value)} placeholder="RG" className="input" />
            </div>
            <AddressBlock value={form.debtorAddr} onChange={(v) => set('debtorAddr', v)} />
          </div>

          <div className="border-t pt-4">
            <div className="flex items-center justify-between mb-2">
              <p className="font-medium text-sm">Avalistas</p>
              <button type="button" onClick={() => set('avalistas', [...(form.avalistas || []), { ...EMPTY_AVALISTA }])} className="text-sm text-primary-600">+ Adicionar avalista</button>
            </div>
            {(form.avalistas || []).map((a: any, idx: number) => (
              <div key={idx} className="border border-gray-100 rounded-lg p-3 mb-3 space-y-3">
                <select defaultValue="" onChange={(e) => pickAval(idx, e.target.value)} className="input">
                  <option value="">Puxar avalista do cadastro de Pessoas...</option>
                  {people.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name} {p.document ? `— ${p.document}` : ''}
                    </option>
                  ))}
                </select>
                <div className="grid md:grid-cols-4 gap-4">
                  <input value={a.name || ''} onChange={(e) => setAval(idx, 'name', e.target.value)} placeholder="Nome do avalista" className="input md:col-span-2" />
                  <select
                    value={a.type || 'FISICA'}
                    onChange={(e) => {
                      const t = e.target.value;
                      setAval(idx, 'type', t);
                      setAval(idx, 'document', maskDoc(a.document || '', t));
                    }}
                    className="input"
                  >
                    <option value="FISICA">Pessoa Física</option>
                    <option value="JURIDICA">Pessoa Jurídica</option>
                  </select>
                  <input value={a.document || ''} onChange={(e) => setAval(idx, 'document', maskDoc(e.target.value, a.type))} placeholder={a.type === 'JURIDICA' ? 'CNPJ' : 'CPF'} className="input" />
                  <input value={a.rg || ''} onChange={(e) => setAval(idx, 'rg', e.target.value)} placeholder="RG" className="input" />
                </div>
                <AddressBlock value={a.addr} onChange={(v) => setAval(idx, 'addr', v)} />
              </div>
            ))}
          </div>

          <label className="block text-sm">Observações
            <textarea value={form.observations} onChange={(e) => set('observations', e.target.value)} className="input mt-1" rows={2} />
          </label>

          <div className="flex justify-end gap-3 border-t pt-4">
            <button onClick={() => setShow(false)} className="px-4 py-2 border border-gray-300 rounded-lg">Cancelar</button>
            <button onClick={create} disabled={saving} className="btn-primary disabled:opacity-50">
              {saving ? 'Gerando...' : 'Gerar parcelamento'}
            </button>
          </div>
        </div>
      )}

      <div className="card">
        {loading ? (
          <div className="py-10 flex justify-center text-gray-400"><Loader className="w-6 h-6 animate-spin" /></div>
        ) : notes.length === 0 ? (
          <p className="text-gray-500 text-sm py-6 text-center">Nenhuma nota promissória. Clique em "Novo parcelamento".</p>
        ) : (
          <div className="divide-y">
            {notes.map((n) => (
              <div key={n.id} className="flex items-center justify-between py-3">
                <div>
                  <p className="font-medium flex items-center gap-2">
                    Nº {n.number} — {n.debtorName || 'Sem devedor'}
                    {n.creditorType === 'TERCEIRO' ? (
                      <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">Particular · só impressão</span>
                    ) : (
                      <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full">Imobiliária · financeiro</span>
                    )}
                    {n.status === 'CANCELED' && (
                      <span className="text-xs bg-gray-200 text-gray-700 px-2 py-0.5 rounded-full">Quitada</span>
                    )}
                  </p>
                  <p className="text-sm text-gray-500">
                    {money(n.totalAmount)} em {n.installments}x de {money(n.installmentAmount)} · {TYPE_LABEL[n.interestType] || n.interestType}
                    {Number(n.interestRate) > 0 ? ` (${Number(n.interestRate)}% a.m.)` : ''}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => print(n.id)}
                    className="flex items-center gap-2 px-3 py-2 bg-primary-600 text-white rounded-lg text-sm hover:bg-primary-700"
                    title="Imprimir as promissórias"
                  >
                    <Printer className="w-4 h-4" /> Imprimir
                  </button>
                  {n.creditorType !== 'TERCEIRO' && n.status !== 'CANCELED' && (
                    <button
                      onClick={() => { setCancelTarget(n); setCancelForm({ reason: '', method: 'PIX', amount: '', discount: '', pin: '' }); }}
                      className="px-3 py-2 border border-emerald-300 text-emerald-700 rounded-lg text-sm hover:bg-emerald-50"
                      title="Quitar/cancelar (registra o recebimento)"
                    >
                      Quitar
                    </button>
                  )}
                  <button
                    onClick={() => { setDelTarget(n); setDelForm({ reason: '', pin: '' }); }}
                    className="p-2 text-gray-500 hover:text-red-600"
                    title="Apagar"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal Apagar */}
      {delTarget && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setDelTarget(null)}>
          <div className="bg-white rounded-xl p-5 w-full max-w-md space-y-3" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-bold text-lg">Apagar nota promissória nº {delTarget.number}</h3>
            <p className="text-sm text-gray-500">Isso desfaz as parcelas e os pagamentos lançados. Exige justificativa e PIN financeiro.</p>
            <textarea value={delForm.reason} onChange={(e) => setDelForm((f) => ({ ...f, reason: e.target.value }))} placeholder="Justificativa" rows={3} className="input" />
            <input type="password" value={delForm.pin} onChange={(e) => setDelForm((f) => ({ ...f, pin: e.target.value }))} placeholder="PIN financeiro (6 dígitos)" maxLength={6} inputMode="numeric" className="input" />
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setDelTarget(null)} className="px-4 py-2 border border-gray-300 rounded-lg">Cancelar</button>
              <button onClick={confirmDelete} disabled={busyAction} className="px-4 py-2 bg-red-600 text-white rounded-lg disabled:opacity-50">
                {busyAction ? 'Apagando...' : 'Apagar e desfazer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Quitar / Cancelar */}
      {cancelTarget && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setCancelTarget(null)}>
          <div className="bg-white rounded-xl p-5 w-full max-w-md space-y-3" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-bold text-lg">Quitar nota promissória nº {cancelTarget.number}</h3>
            <p className="text-sm text-gray-500">Registra o recebimento das parcelas em aberto. Dinheiro entra no caixa; PIX/TED/cartão entram na conta corrente.</p>
            <div className="grid grid-cols-2 gap-3">
              <label className="text-sm">Forma de recebimento
                <select value={cancelForm.method} onChange={(e) => setCancelForm((f) => ({ ...f, method: e.target.value }))} className="input mt-1">
                  <option value="CASH">Dinheiro (caixa)</option>
                  <option value="PIX">PIX (conta)</option>
                  <option value="BANK_TRANSFER">TED/Transferência (conta)</option>
                  <option value="CREDIT_CARD">Cartão de crédito (conta)</option>
                  <option value="DEBIT_CARD">Cartão de débito (conta)</option>
                  <option value="CHECK">Cheque (caixa)</option>
                </select>
              </label>
              <label className="text-sm">Desconto (R$)
                <input value={cancelForm.discount} onChange={(e) => setCancelForm((f) => ({ ...f, discount: e.target.value }))} placeholder="0,00" className="input mt-1" />
              </label>
            </div>
            <label className="text-sm block">Valor recebido (R$) — deixe em branco para usar o saldo em aberto menos o desconto
              <input value={cancelForm.amount} onChange={(e) => setCancelForm((f) => ({ ...f, amount: e.target.value }))} placeholder="opcional" className="input mt-1" />
            </label>
            <textarea value={cancelForm.reason} onChange={(e) => setCancelForm((f) => ({ ...f, reason: e.target.value }))} placeholder="Justificativa da quitação (ex.: acordo com desconto)" rows={2} className="input" />
            <input type="password" value={cancelForm.pin} onChange={(e) => setCancelForm((f) => ({ ...f, pin: e.target.value }))} placeholder="PIN financeiro (6 dígitos)" maxLength={6} inputMode="numeric" className="input" />
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setCancelTarget(null)} className="px-4 py-2 border border-gray-300 rounded-lg">Fechar</button>
              <button onClick={confirmCancel} disabled={busyAction} className="px-4 py-2 bg-emerald-600 text-white rounded-lg disabled:opacity-50">
                {busyAction ? 'Quitando...' : 'Quitar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
